import { AnimationEvent, usePopup } from '@libs/hooks';
import { TPopupProps } from '@libs/types';
import { Portal } from '@libs/utils';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useEffectEvent, useState } from 'react';
import { flushSync } from 'react-dom';
import { zIndex } from '../utils/constants';

const PopupBox = styled('div')<{ $animated?: boolean }>((props) => ({
    '@keyframes getOutAnimation': {
        '0%': {
            transform: 'translateY(0)',
        },
        '100%': {
            transform: 'translateY(90vh)',
        },
    },
    animationName: props.$animated ? 'getOutAnimation' : '',
    animationDuration: '0.6s',
    animationDelay: '0.1s',
    animationTimingFunction: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    animationIterationCount: 1,
    overflow: 'hidden',
    bottom: '24px',
    maxWidth: '568px',
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',

    '@media screen and (min-width: 708px)': {
        bottom: 'auto',
        borderRadius: '16px',
        maxHeight: 'calc(100vh - 96px)',
    },
}));

const PopupOuter = styled('div')({
    alignItems: 'flex-end',
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    padding: '0 16px',
    '@media screen and (min-width: 708px)': {
        alignItems: 'center',
    },
});

const Overlay = styled('div')({
    alignItems: 'center',
    background: 'rgba(0, 0, 0, 0.48)',
    bottom: 0,
    display: 'block',
    justifyContent: 'center',
    left: 0,
    position: 'fixed',
    right: 0,
    top: 0,
    zIndex: zIndex.overlay,
});

export const Popup = ({ content, modal, setPopup }: TPopupProps) => {
    const [animated, setAnimated] = useState(false);
    const [data, setData] = useState<typeof content>(null);
    const { handlers, overlayRef, overlayClick } = usePopup(data, setAnimated, modal);

    const updatePopup = useEffectEvent((currContent: React.ReactNode, currData: React.ReactNode) => {
        if (currContent) {
            setData(currContent);
        } else if (currData) {
            setAnimated(true);
        }
    });

    useEffect(() => {
        updatePopup(content, data);
    }, [content, data]);

    const closeAnimationPopup = useCallback(
        (e: AnimationEvent<HTMLDivElement>) => {
            if (e?.animationName === 'getOutAnimation') {
                flushSync(() => {
                    setData(null);
                    setAnimated(false);
                    setPopup(null);
                });
            }
        },
        [setPopup],
    );

    if (data) {
        return (
            <Portal>
                <div id="popup" {...handlers}>
                    <Overlay onClick={overlayClick}>
                        <PopupOuter>
                            <PopupBox $animated={animated} onAnimationEnd={closeAnimationPopup} ref={overlayRef}>
                                {data}
                            </PopupBox>
                        </PopupOuter>
                    </Overlay>
                </div>
            </Portal>
        );
    }
    return null;
};
